import React from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * Component that parses text and converts "Contact Us" / "تواصل معنا" to styled links
 * Also handles "contact form" / "نموذج التواصل" variations
 * 
 * @param {string} text - The text to parse and convert
 * @param {string} className - Additional CSS classes
 * @param {function} onContactFormClick - Optional callback when clicking "contact form" on contact page
 */
const ContactUsLink = ({ text, className = "", onContactFormClick }) => {
  const location = useLocation();
  const isOnContactPage = location.pathname === "/contact-us";
  if (!text) return null;

  // Patterns to match in English and Arabic
  const patterns = [
    { en: "Contact Us", ar: "تواصل معنا" },
    { en: "contact form", ar: "نموذج التواصل" },
    { en: "contact us", ar: "تواصل معنا" },
  ];

  // Determine language by checking if text contains Arabic characters
  const isArabic = /[\u0600-\u06FF]/.test(text);
  const lang = isArabic ? "ar" : "en";

  // Find which pattern matches
  let matchedPattern = null;
  let matchText = null;

  for (const pattern of patterns) {
    const searchText = lang === "ar" ? pattern.ar : pattern.en;
    if (text.includes(searchText)) {
      matchedPattern = pattern;
      matchText = searchText;
      break;
    }
  }

  if (!matchedPattern) {
    // No match found, return text as-is
    return <span className={className}>{text}</span>;
  }

  // Split text and create links for all occurrences
  const parts = text.split(matchText);
  const result = [];

  // Check if this is "contact form" pattern and we're on contact page
  const isContactFormPattern = matchText.toLowerCase().includes("form") || 
                               matchText.includes("نموذج");

  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) {
      result.push(parts[i]);
    }
    // Add link after each part except the last one
    if (i < parts.length - 1) {
      // If on contact page and clicking "contact form", use callback instead of Link
      if (isOnContactPage && isContactFormPattern && onContactFormClick) {
        result.push(
          <a
            key={`contact-link-${i}`}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onContactFormClick();
            }}
            className="contactUsLink"
          >
            {matchText}
          </a>
        );
      } else {
        result.push(
          <Link key={`contact-link-${i}`} to="/contact-us" className="contactUsLink">
            {matchText}
          </Link>
        );
      }
    }
  }

  return <span className={className}>{result}</span>;
};

export default ContactUsLink;
